import { useEffect } from 'react';

import { clientConfig } from '@/config/clientConfig';
import { useNotices } from '@/features/notices/useNotices';
import { ProfileType } from '@/shared/api';
import { EncryptionType, KeyPair } from '@/shared/lib/secure-json/core/crypto-core/types';
import { useCollectionsStatusStore } from '@/shared/lib/sj-orm/stores/collectionsStatus.store';
import { ErrorInit } from '@/shared/lib/sj-orm/synchronizer/components/ErrorInit';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { log } from '@/shared/utils/log';

import { useCollectionsInit } from './useCollectionsInit';

export const useCollectionsInitializer = () => {
  const notices = useNotices();

  const [keyPair, encryptionType] = useKeyPairStore((s) => [s.keyPair, s.encryptionType]);
  const profileStore = useProfileStore();
  const colStatusStore = useCollectionsStatusStore();
  const colInit = useCollectionsInit();

  const profileEncryption = profileStore.encryptionType;

  const fetchCollections = async (pair: KeyPair, encryption: EncryptionType) => {
    log.trace('[CollectionsContainer] fetchCollections');
    const res = await colInit.initialFetchCollections(pair, encryption);

    if (res.error) {
      const list = res.collections ?? [];
      const items = list.slice(0, 3);
      const more = list.slice(3);

      if (more.length) {
        items.push(`+${more.length} more`);
      }

      notices.error(<ErrorInit items={items} />, { delay: 5 });
    }
  };

  //#region WebApp init
  useEffect(() => {
    if (clientConfig.isExported) {
      return;
    }

    log.trace('[CollectionsContainer] useEffect webapp'); // TODO remove
    if (!keyPair || !profileEncryption) {
      return;
    }

    if (colStatusStore.isReadyForInit && colStatusStore.status === 'none') {
      fetchCollections(keyPair, profileEncryption);
    }
  }, [colInit, colStatusStore.status, colStatusStore.isReadyForInit, keyPair, profileEncryption]);

  //#region Myhub Init
  useEffect(() => {
    if (!clientConfig.isExported) {
      return;
    }

    log.trace('[CollectionsContainer] useEffect myhub'); // TODO remove
    if (!keyPair || !encryptionType) {
      return;
    }

    if (colStatusStore.isReadyForInit && colStatusStore.status === 'none') {
      fetchCollections(keyPair, encryptionType);
    }
  }, [colInit, colStatusStore.status, colStatusStore.isReadyForInit, keyPair, encryptionType]);

  useEffect(() => {
    if (!clientConfig.isExported) {
      return;
    }

    if (!keyPair || !encryptionType) {
      return;
    }

    if (!colStatusStore.isReadyForInit && colStatusStore.status === 'none') {
      profileStore.setProfileType(ProfileType.MainUser);
      colStatusStore.setReadyForInit();
    }
  }, [colStatusStore.isReadyForInit, colStatusStore.status, keyPair, encryptionType]);
};
